import React, { useState, useEffect, useRef } from 'react'
import { Link } from 'gatsby'
import { useLocation } from '@reach/router'
import PropTypes from 'prop-types'
import qs from 'query-string'
import { useIntl, FormattedMessage } from 'react-intl'
import { Configure, InstantSearch, Pagination, ScrollTo, Stats } from 'react-instantsearch-dom'
import algoliasearch from 'algoliasearch/lite'

import CurrentRefinements from '~components/search/current-refinements'
import Hits from '~components/search/hits'
import SearchBox from '~components/search/search-box'
import Filters from '~components/search/filters'

const Search = ({ type: pageType, displayStyle, recommendedSearchTerms }) => {
  const { locale, messages } = useIntl()
  const location = useLocation()

  const searchClient = algoliasearch(process.env.GATSBY_ALGOLIA_APPLICATION_ID, process.env.GATSBY_ALGOLIA_KEY)
  let searchIndex = ''
  switch (locale) {
    case 'en-US':
      searchIndex = 'webArticlesEnglish'
      break
    case 'es-MX':
      searchIndex = 'webArticlesSpanish'
      break
    default:
      searchIndex = 'webArticlesEnglish'
      break
  }

  // Basic search query parameters (read-only)
  const { search } = location
  const searchParams = qs.parse(search)
  const {
    q: searchQuery = '',
    ages = null,
    topics = null,
    types: contentTypes = null,
    activityTypes = null
  } = searchParams

  // Set starting query to query present in search parameters
  const [query, setQuery] = useState(searchQuery)

  // Update state of query when query parameters change
  useEffect(() => {
    setQuery(searchQuery)
  }, [location]) // eslint-disable-line react-hooks/exhaustive-deps

  let searchPlaceholder = ''
  // Set appropriate search placeholder
  switch (pageType) {
    case 'All pages':
      searchPlaceholder = messages['search-all-pages']
      break
    case 'Activity':
      searchPlaceholder = messages['search-activities']
      break
    case 'Video':
      searchPlaceholder = messages['search-videos']
      break
    default:
      break
  }

  // Hide required "hasQuestion" filter on faq/trending questions page
  let hideFirst = false
  if (pageType === 'Question') {
    hideFirst = true
  }

  return (
    <InstantSearch searchClient={searchClient} indexName={searchIndex}>
      <Configure hitsPerPage={displayStyle !== 'question' ? 18 : 999} distinct />
      <div className='flex flex-col lg:flex-row -mx-4'>
        <Filters
          ages={ages}
          topics={topics}
          contentTypes={contentTypes}
          activityTypes={activityTypes}
          pageType={pageType}
        />
        <div className='lg:w-3/4 px-4 pt-8'>
          {displayStyle !== 'question' && (
            <SearchBox
              placeholder={searchPlaceholder}
              showLoadingIndicator
              recommendedSearchTerms={recommendedSearchTerms}
              query={query}
            />
          )}
          <ScrollTo>
            <CurrentRefinements hideFirst={hideFirst} />
            <Hits displayStyle={displayStyle} />
          </ScrollTo>
          {displayStyle !== 'question' && (
            <>
              <Pagination showLast />
              <Stats
                translations={{
                  stats(nbHits) {
                    return (
                      <p className='mt-4 mb-8 text-lg text-gray-dark text-center'>
                        {nbHits === 0 ? 'No' : nbHits} <FormattedMessage id='results-found' />
                      </p>
                    )
                  }
                }}
              />
            </>
          )}
        </div>
      </div>
    </InstantSearch>
  )
}

Search.defaultProps = {
  displayStyle: 'list'
}

Search.propTypes = {
  displayStyle: PropTypes.string,
  recommendedSearchTerms: PropTypes.array,
  type: PropTypes.string
}

export default Search
